// Doc 08 §8 — Unified share dialog for trips and templates.
// Props-in / events-out; all state lives in the shareStore.
//
// Sections (each its own component below):
//   1. InvitePeopleSection: friend filter + email-invite + managed-profile affordances (§3)
//   2. MembersSection: role dropdowns (owners), remove, managed badge (§1, §3)
//   3. PendingInvitesSection: email invites with revoke (§3)
//   4. LinksSection: create (view-only vs joinable, role, expiry), list, copy/revoke (§4)
//   5. CapPrompt: member cap → switch-to-link flow (§6–7)

import { type ChangeEvent, type CSSProperties, type ReactNode, useState } from "react";
import { Avatar, Button, Dialog, vars } from "@pack-it-up/ui-web";
import type { InviteLink, MemberEntry, PendingInvite, Role } from "@pack-it-up/core";
import { MAX_ENUMERATED_MEMBERS, type ShareClassification } from "@pack-it-up/viewmodel";
import { LinkIcon, SearchIcon } from "../../app/icons.js";
import { EditAffordance, editableFieldStyle } from "../../pages/shared/ListTopbar.js";
import { editableHover } from "../../pages/shared/ListTopbar.css.js";

// ── Helpers ───────────────────────────────────────────────────────────────────

const ROLES: Role[] = ["viewer", "editor"];
const ROLE_LABELS: Record<Role, string> = { owner: "Owner", editor: "Can edit", viewer: "Can view" };

const sectionStyle: CSSProperties = {
  padding: `${vars.space.md} 0`,
  borderTop: `1px solid ${vars.color.border}`,
};

const sectionLabel: CSSProperties = {
  fontSize: vars.font.sizeXs,
  fontWeight: vars.font.weightSemibold,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: vars.color.textMuted,
  marginBottom: vars.space.sm,
};

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: vars.space.sm,
  padding: `${vars.space.xs} 0`,
};

const nameStyle: CSSProperties = { fontSize: vars.font.sizeSm, fontWeight: vars.font.weightMedium };
const subStyle: CSSProperties = { fontSize: vars.font.sizeXs, color: vars.color.textMuted, marginTop: "1px" };

const inputStyle: CSSProperties = {
  flex: 1,
  background: "transparent",
  border: "none",
  outline: "none",
  color: vars.color.text,
  fontFamily: vars.font.body,
  fontSize: vars.font.sizeSm,
};

const searchBoxStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: vars.space.sm,
  background: vars.color.surfaceRaised,
  border: `1px solid ${vars.color.border}`,
  borderRadius: vars.radius.sm,
  padding: `${vars.space.sm} ${vars.space.md}`,
};

const selectStyle: CSSProperties = {
  background: vars.color.surface,
  border: `1px solid ${vars.color.border}`,
  borderRadius: vars.radius.sm,
  padding: "5px 8px",
  fontFamily: vars.font.body,
  fontSize: vars.font.sizeSm,
  color: vars.color.text,
  cursor: "pointer",
  flexShrink: 0,
};

const mutedBtn: CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: vars.color.textMuted,
  fontSize: vars.font.sizeXs,
  fontFamily: vars.font.body,
  padding: 0,
  flexShrink: 0,
};

const dangerBtn: CSSProperties = {
  ...mutedBtn,
  color: vars.color.danger,
};

const accentBtn: CSSProperties = {
  ...mutedBtn,
  color: vars.color.accent,
  fontWeight: vars.font.weightMedium,
};

// ── RoleSelect ────────────────────────────────────────────────────────────────

// The one role picker: candidate rows, member rows and the link form all use it.
const RoleSelect = ({ value, onChange, ariaLabel }: { value: Role; onChange: (r: Role) => void; ariaLabel: string }): ReactNode => (
  <select
    value={value}
    onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value as Role)}
    aria-label={ariaLabel}
    style={selectStyle}
  >
    {ROLES.map((r) => (
      <option key={r} value={r}>
        {ROLE_LABELS[r]}
      </option>
    ))}
  </select>
);

// ── CandidateRow ──────────────────────────────────────────────────────────────

// One affordance row for everything the search can classify to: a friend match,
// an email invite, or a managed profile — the variations are props.
interface CandidateRowProps {
  title: string;
  subtitle: string;
  actionLabel: string;
  onAction: () => void;
  /** Avatar for people that already exist; the email affordance has none. */
  avatarName?: string;
  /** Accent styling for the "this will send an invite" affordance. */
  accent?: boolean;
  /** Role picker for the would-be member; managed profiles don't carry a role. */
  roleSelect?: { value: Role; onChange: (r: Role) => void; ariaLabel: string };
}

const CandidateRow = ({ title, subtitle, actionLabel, onAction, avatarName, accent = false, roleSelect }: CandidateRowProps): ReactNode => (
  <div
    style={{
      ...rowStyle,
      background: accent ? vars.color.accentSubtle : vars.color.surfaceRaised,
      borderRadius: vars.radius.sm,
      padding: vars.space.sm,
    }}
  >
    {avatarName !== undefined && <Avatar name={avatarName} size="sm" />}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ ...nameStyle, ...(accent ? { color: vars.color.accent } : {}) }}>{title}</div>
      <div style={subStyle}>{subtitle}</div>
    </div>
    {roleSelect !== undefined && <RoleSelect {...roleSelect} />}
    <button type="button" style={accentBtn} onClick={onAction}>
      {actionLabel}
    </button>
  </div>
);

// ── InvitePeopleSection ───────────────────────────────────────────────────────

interface InvitePeopleSectionProps {
  classify: (text: string) => ShareClassification;
  onAddFriend: (userId: string, role: Role) => Promise<void>;
  onInviteByEmail: (email: string, role: Role) => Promise<void>;
  onAddManaged: (profileId: string) => Promise<void>;
}

const InvitePeopleSection = ({ classify, onAddFriend, onInviteByEmail, onAddManaged }: InvitePeopleSectionProps): ReactNode => {
  const [query, setQuery] = useState("");
  const [selectedRole, setSelectedRole] = useState<Role>("editor");

  const classification = classify(query);
  const act = async (fn: () => Promise<void>): Promise<void> => {
    await fn();
    setQuery("");
  };

  return (
    <div>
      <div style={sectionLabel}>Invite people</div>
      {/* Search box */}
      <div style={searchBoxStyle}>
        {SearchIcon(13)}
        <input
          type="text"
          placeholder="Search friends by name or email…"
          value={query}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          aria-label="Search people to invite"
          style={inputStyle}
          autoComplete="off"
        />
      </div>

      {/* Affordances — exactly one per classification bucket */}
      {query.trim() !== "" && (
        <div style={{ marginTop: vars.space.sm, display: "flex", flexDirection: "column", gap: vars.space.xs }}>
          {classification.kind === "friend" &&
            classification.matches.map((f) => (
              <CandidateRow
                key={f.userId}
                avatarName={f.displayName}
                title={f.displayName}
                subtitle={f.email}
                actionLabel="Add"
                roleSelect={{ value: selectedRole, onChange: setSelectedRole, ariaLabel: "Role for this person" }}
                onAction={() => { void act(() => onAddFriend(f.userId, selectedRole)); }}
              />
            ))}

          {classification.kind === "email" && (
            <CandidateRow
              accent
              title={`Invite "${classification.email}"`}
              subtitle="They'll receive an email to sign up and join."
              actionLabel="Invite"
              roleSelect={{ value: selectedRole, onChange: setSelectedRole, ariaLabel: "Role for email invite" }}
              onAction={() => { void act(() => onInviteByEmail(classification.email, selectedRole)); }}
            />
          )}

          {classification.kind === "managed" &&
            classification.matches.map((m) => (
              <CandidateRow
                key={m.id}
                avatarName={m.displayName}
                title={m.displayName}
                subtitle="Managed profile — no account needed"
                actionLabel="Add"
                onAction={() => { void act(() => onAddManaged(m.id)); }}
              />
            ))}

          {classification.kind === "invalid" && (
            <div style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted, padding: `${vars.space.xs} ${vars.space.sm}` }}>
              No matches — try a different name or email.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── MembersSection ────────────────────────────────────────────────────────────

interface MemberRowProps {
  member: MemberEntry;
  isOwner: boolean;   // the viewer is the owner → can change roles
  onChangeRole: ((role: Role) => void) | undefined;
  onRemove: (() => void) | undefined;
  /** Guests only (T31): their name lives here and nowhere else, so the owner can fix it. */
  onRename: ((displayName: string) => void) | undefined;
}

// Click-to-edit guest name — the topbar's inline-edit pattern (`EditAffordance`:
// check to save, X to cancel), scoped to the one name the trip actually owns.
const GuestName = ({ name, onRename }: { name: string; onRename: (displayName: string) => void }): ReactNode => {
  const [draft, setDraft] = useState<string | null>(null);

  const commit = (): void => {
    const trimmed = (draft ?? "").trim();
    setDraft(null);
    // An empty name would leave the guest unidentifiable; treat it as a cancel.
    if (trimmed !== "" && trimmed !== name) onRename(trimmed);
  };

  if (draft === null) {
    return (
      <button
        type="button"
        className={editableHover}
        style={{ ...editableFieldStyle, ...nameStyle, background: "none", border: "none", font: "inherit", textAlign: "left" }}
        aria-label={`Rename ${name}`}
        onClick={() => setDraft(name)}
      >
        {name}
      </button>
    );
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "2px" }}>
      <input
        autoFocus
        aria-label={`Name for ${name}`}
        value={draft}
        maxLength={60}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setDraft(null);
        }}
        style={{
          ...editableFieldStyle,
          cursor: "text",
          background: vars.color.surfaceRaised,
          outline: `1px solid ${vars.color.accent}`,
          color: vars.color.text,
          fontFamily: "inherit",
          fontSize: vars.font.sizeSm,
          width: "140px",
        }}
      />
      <EditAffordance onSave={commit} onCancel={() => setDraft(null)} />
    </span>
  );
};

const MemberRow = ({ member, isOwner, onChangeRole, onRemove, onRename }: MemberRowProps): ReactNode => {
  const name = member.profile.displayName;
  const email = member.kind === "user" ? member.profile.email : undefined;
  const role = member.kind === "user" ? member.role : undefined;
  const isManaged = member.kind === "managed";
  // T31 — a link guest has no account, so no email to show under its name; say what it
  // is instead, since its access ends when the link is revoked.
  const isGuest = member.kind === "user" && member.guest !== undefined;

  return (
    <div style={rowStyle}>
      <Avatar name={name} size="sm" />
      <div style={{ flex: 1, minWidth: 0 }}>
        {isGuest && onRename !== undefined ? (
          <GuestName name={name} onRename={onRename} />
        ) : (
          <div style={nameStyle}>{name}</div>
        )}
        {isGuest ? (
          <div style={{ ...subStyle, color: vars.color.accent }}>Guest via link</div>
        ) : (
          email !== undefined && <div style={subStyle}>{email}</div>
        )}
        {isManaged && <div style={{ ...subStyle, color: vars.color.accent }}>Managed by you</div>}
      </div>
      {role === "owner" ? (
        <span style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted, flexShrink: 0 }}>Owner</span>
      ) : role !== undefined && isOwner && onChangeRole !== undefined ? (
        <RoleSelect value={role} onChange={onChangeRole} ariaLabel={`Change role for ${name}`} />
      ) : (
        role !== undefined && (
          <span style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted, flexShrink: 0 }}>
            {ROLE_LABELS[role]}
          </span>
        )
      )}
      {isOwner && onRemove !== undefined && (isManaged || role !== "owner") && (
        <button type="button" style={dangerBtn} onClick={onRemove} aria-label={`Remove ${name}`}>
          Remove
        </button>
      )}
    </div>
  );
};

interface MembersSectionProps {
  members: Record<string, MemberEntry>;
  actorId: string;
  isOwner: boolean;
  onChangeRole: (userId: string, role: Role) => Promise<void>;
  onRemoveMember: (userId: string) => Promise<void>;
  onRenameGuest: (userId: string, displayName: string) => Promise<void>;
}

const MembersSection = ({ members, actorId, isOwner, onChangeRole, onRemoveMember, onRenameGuest }: MembersSectionProps): ReactNode => (
  <div style={sectionStyle}>
    <div style={sectionLabel}>People with access</div>
    {Object.entries(members).map(([participantId, member]) => (
      <MemberRow
        key={participantId}
        member={member}
        isOwner={isOwner}
        onChangeRole={isOwner ? (role) => { void onChangeRole(participantId, role); } : undefined}
        onRemove={isOwner && participantId !== actorId ? () => { void onRemoveMember(participantId); } : undefined}
        // The owner can fix any guest's name; a guest can always fix its own (the rules
        // carve that one field out via `onlyOwnGuestNameChanged`). Nobody else.
        onRename={
          isOwner || participantId === actorId
            ? (displayName) => { void onRenameGuest(participantId, displayName); }
            : undefined
        }
      />
    ))}
  </div>
);

// ── PendingInvitesSection ─────────────────────────────────────────────────────

interface PendingInvitesSectionProps {
  invites: PendingInvite[];
  isOwner: boolean;
  onRevoke: (inviteId: string) => Promise<void>;
}

const PendingInvitesSection = ({ invites, isOwner, onRevoke }: PendingInvitesSectionProps): ReactNode => {
  if (invites.length === 0) return null;
  return (
    <div style={sectionStyle}>
      <div style={sectionLabel}>Pending invites</div>
      {invites.map((invite) => (
        <div key={invite.id} style={rowStyle}>
          <Avatar name={invite.email.slice(0, 1).toUpperCase()} size="sm" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={nameStyle}>{invite.email}</div>
            <div style={{ ...subStyle, fontStyle: "italic" }}>Invited — pending ({ROLE_LABELS[invite.role]})</div>
          </div>
          {isOwner && (
            <button type="button" style={dangerBtn} onClick={() => { void onRevoke(invite.id); }} aria-label={`Revoke invite to ${invite.email}`}>
              Revoke
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

// ── LinksSection ──────────────────────────────────────────────────────────────

interface LinkRowProps {
  link: InviteLink;
  isOwner: boolean;
  onRevoke: () => void;
}

const LinkRow = ({ link, isOwner, onRevoke }: LinkRowProps): ReactNode => {
  // Environment-supplied hostname so links work on localhost / previews / prod alike.
  const path = `/join/${link.token}`;
  const displayUrl = `${window.location.host}${path}`;
  const modeLabel = link.allowJoin ? "Joinable" : "View-only";
  const expiry = link.expiresAt !== undefined ? `Expires ${link.expiresAt.toLocaleDateString()}` : "No expiry";

  return (
    <div style={{ ...rowStyle, alignItems: "flex-start", flexDirection: "column", gap: vars.space.xs }}>
      <div style={{ display: "flex", alignItems: "center", gap: vars.space.sm, width: "100%" }}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: vars.space.xs }}>
          {LinkIcon}
          <span style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {displayUrl}
          </span>
        </div>
        <button
          type="button"
          style={accentBtn}
          onClick={() => { void navigator.clipboard.writeText(`${window.location.origin}${path}`); }}
          aria-label="Copy link"
        >
          Copy
        </button>
        {isOwner && (
          <button type="button" style={dangerBtn} onClick={onRevoke} aria-label="Revoke link">
            Revoke
          </button>
        )}
      </div>
      <div style={{ display: "flex", gap: vars.space.sm, fontSize: vars.font.sizeXs, color: vars.color.textMuted }}>
        <span>{modeLabel}</span>
        <span>·</span>
        <span>{ROLE_LABELS[link.role]}</span>
        <span>·</span>
        <span>{expiry}</span>
      </div>
    </div>
  );
};

interface CreateLinkFormProps {
  onCreate: (role: Role, opts: { allowJoin: boolean; expiresAt?: Date }) => void;
}

// Expiry choices (Doc 08 §4: optional `expiresAt` gives time-boxed links).
const EXPIRY_OPTIONS = [
  { value: "", label: "No expiry" },
  { value: "1", label: "Expires in 1 day" },
  { value: "7", label: "Expires in 7 days" },
  { value: "30", label: "Expires in 30 days" },
] as const;

// T31 — joining follows from the role, so there's no separate "Allow joining" toggle:
// a viewer link only ever shows the trip (nothing to join for), and an editor link must
// be joinable, because editing is what membership buys. An anonymous visitor joins an
// editor link as a named guest.
export const allowJoinForRole = (role: Role): boolean => role !== "viewer";

const CreateLinkForm = ({ onCreate }: CreateLinkFormProps): ReactNode => {
  const [role, setRole] = useState<Role>("viewer");
  const [expiryDays, setExpiryDays] = useState("");

  const handleCreate = (): void => {
    const days = expiryDays === "" ? null : Number(expiryDays);
    onCreate(role, {
      allowJoin: allowJoinForRole(role),
      ...(days !== null ? { expiresAt: new Date(Date.now() + days * 24 * 60 * 60 * 1000) } : {}),
    });
  };

  return (
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: vars.space.sm, marginTop: vars.space.sm }}>
      <RoleSelect value={role} onChange={setRole} ariaLabel="Link role" />
      <span style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted }}>
        {allowJoinForRole(role) ? "Joiners become members" : "Anyone with the link can look, not join"}
      </span>
      <select
        value={expiryDays}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => setExpiryDays(e.target.value)}
        aria-label="Link expiry"
        style={selectStyle}
      >
        {EXPIRY_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <Button size="sm" onClick={handleCreate}>
        Create link
      </Button>
    </div>
  );
};

interface LinksSectionProps {
  links: InviteLink[];
  isOwner: boolean;
  onCreateLink: (role: Role, opts: { allowJoin: boolean; expiresAt?: Date }) => Promise<void>;
  onRevokeLink: (token: string) => Promise<void>;
}

const LinksSection = ({ links, isOwner, onCreateLink, onRevokeLink }: LinksSectionProps): ReactNode => (
  <div style={sectionStyle}>
    <div style={sectionLabel}>Invite links</div>
    {links.length === 0 && (
      <div style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted, marginBottom: vars.space.sm }}>
        No active links.
      </div>
    )}
    {links.filter((l) => !l.revoked).map((link) => (
      <div key={link.token} style={{ marginBottom: vars.space.sm }}>
        <LinkRow
          link={link}
          isOwner={isOwner}
          onRevoke={() => { void onRevokeLink(link.token); }}
        />
      </div>
    ))}
    {isOwner && <CreateLinkForm onCreate={(role, opts) => { void onCreateLink(role, opts); }} />}
  </div>
);

// ── Cap prompt ─────────────────────────────────────────────────────────────────

const CapPrompt = ({ onSwitchToLink }: { onSwitchToLink: () => void }): ReactNode => (
  <div
    style={{
      padding: vars.space.md,
      background: vars.color.accentSubtle,
      borderRadius: vars.radius.md,
      marginBottom: vars.space.md,
    }}
    role="alert"
    aria-live="polite"
  >
    <div style={{ fontSize: vars.font.sizeSm, fontWeight: vars.font.weightMedium, color: vars.color.text }}>
      Member limit reached ({MAX_ENUMERATED_MEMBERS} members)
    </div>
    <div style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted, marginTop: "2px" }}>
      To keep sharing, switch to an invite link — anyone with the link can join without being enumerated.
    </div>
    <button type="button" style={{ ...accentBtn, marginTop: vars.space.sm }} onClick={onSwitchToLink}>
      Create an invite link instead →
    </button>
  </div>
);

// ── ShareDialog ───────────────────────────────────────────────────────────────

export interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  resourceName: string;
  /** Current members map from the trip/template */
  members: Record<string, MemberEntry>;
  /**
   * Issue #188 — a template's travelers, rendered as their own section below the
   * members. The two lists answer different questions: who may *edit* this template
   * (members, above) versus who is going on the trip it describes (travelers). A trip
   * doesn't pass this: its members are its travelers.
   */
  travelers?: ReactNode;
  /** The signed-in user's ID — determines owner actions */
  actorId: string;
  /** Owner/editor only (Doc 08) — a viewer (incl. an anonymous grant-holder) can't invite anyone. */
  canManage: boolean;
  pendingInvites: PendingInvite[];
  links: InviteLink[];
  capPrompt: boolean;
  /** Classify the current search text against friends + managed profiles */
  classify: (text: string) => ShareClassification;
  onShareWithUser: (userId: string, role: Role) => Promise<void>;
  onInviteByEmail: (email: string, role: Role) => Promise<void>;
  onRevokePendingInvite: (inviteId: string) => Promise<void>;
  onAddManagedMember: (profileId: string) => Promise<void>;
  onChangeRole: (userId: string, role: Role) => Promise<void>;
  onRemoveMember: (userId: string) => Promise<void>;
  /** T31 — rename a link guest (owner-only; a guest's name exists only on this trip). */
  onRenameGuest: (userId: string, displayName: string) => Promise<void>;
  onCreateLink: (role: Role, opts: { allowJoin: boolean; expiresAt?: Date }) => Promise<void>;
  onRevokeLink: (token: string) => Promise<void>;
}

export const ShareDialog = ({
  open,
  onClose,
  resourceName,
  members,
  travelers,
  actorId,
  canManage,
  pendingInvites,
  links,
  capPrompt,
  classify,
  onShareWithUser,
  onInviteByEmail,
  onRevokePendingInvite,
  onAddManagedMember,
  onChangeRole,
  onRemoveMember,
  onRenameGuest,
  onCreateLink,
  onRevokeLink,
}: ShareDialogProps): ReactNode => {
  const actorMember = members[actorId];
  const isOwner = actorMember?.kind === "user" && actorMember.role === "owner";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={canManage ? `Share "${resourceName}"` : `Who's on "${resourceName}"`}
      subtitle={canManage ? "Invite people or share a link." : "Everyone with access to this trip."}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {capPrompt && (
          <div style={{ paddingBottom: vars.space.md }}>
            <CapPrompt onSwitchToLink={() => { /* scroll to links section */ }} />
          </div>
        )}

        {canManage && (
          <InvitePeopleSection
            classify={classify}
            onAddFriend={onShareWithUser}
            onInviteByEmail={onInviteByEmail}
            onAddManaged={onAddManagedMember}
          />
        )}

        <MembersSection
          members={members}
          actorId={actorId}
          isOwner={isOwner}
          onChangeRole={onChangeRole}
          onRemoveMember={onRemoveMember}
          onRenameGuest={onRenameGuest}
        />

        {travelers !== undefined && <div style={sectionStyle}>{travelers}</div>}

        {/* Nothing about inviting for someone who can't invite (an anonymous guest
            reaches this dialog through the travellers pill just to see who's here and
            fix its own name). Both sections are empty for them anyway — the store
            doesn't subscribe to the owner/editor-only streams behind them. */}
        {canManage && (
          <>
            <PendingInvitesSection
              invites={pendingInvites}
              isOwner={isOwner}
              onRevoke={onRevokePendingInvite}
            />

            <LinksSection
              links={links}
              isOwner={isOwner}
              onCreateLink={onCreateLink}
              onRevokeLink={onRevokeLink}
            />
          </>
        )}
      </div>
    </Dialog>
  );
};
